import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import StatusBadge from "../components/StatusBadge";
import api from "../lib/api";
import { downloadAuthed } from "../lib/download";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Download, FileSignature } from "lucide-react";

export default function Exports() {
  const [exports, setExports] = useState([]);
  const [finals, setFinals] = useState([]);
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({ final_order_number: "", final_order_date: "", signing_status: "Pending", cms_upload_status: "Pending", record_status: "Final" });

  const reload = () => {
    api.get("/exports").then((r) => setExports(r.data));
    api.get("/final-orders").then((r) => setFinals(r.data));
  };
  useEffect(reload, []);

  const dl = async (id, fname) => {
    try {
      await downloadAuthed(`/exports/${id}/download`, fname);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const saveFinal = async () => {
    try {
      await api.post("/final-orders", { export_id: target.export_id, ...form });
      toast.success("Final order recorded");
      setTarget(null);
      setForm({ final_order_number: "", final_order_date: "", signing_status: "Pending", cms_upload_status: "Pending", record_status: "Final" });
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <Layout breadcrumbs={["SSRD", "Exports"]}>
      <div className="mb-6">
        <div className="text-[10px] tracking-widest text-muted-foreground uppercase">Final Output</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="exports-title">Exports & Final Order Record</h1>
      </div>

      <Card className="mb-6" data-testid="exports-table-card"><CardContent className="p-0">
        <div className="px-5 py-3 border-b border-border"><div className="text-lg font-semibold">Exported Files</div></div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>File</TableHead><TableHead>Type</TableHead>
            <TableHead>Case</TableHead><TableHead>Exported By</TableHead>
            <TableHead>At</TableHead><TableHead className="text-right">Action</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {exports.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground py-8">No exports yet.</TableCell></TableRow>}
            {exports.map((e) => (
              <TableRow key={e.export_id}>
                <TableCell className="text-xs font-mono">{e.file_name}</TableCell>
                <TableCell><StatusBadge status={e.export_type} /></TableCell>
                <TableCell className="font-mono text-[11px]">{e.case_id.slice(0, 8)}</TableCell>
                <TableCell className="text-xs">{e.exported_by_user_name}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{(e.exported_at || "").replace("T", " ").slice(0, 16)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => dl(e.export_id, e.file_name)} className="gap-1" data-testid={`download-${e.export_id}`}><Download className="size-3.5" /> Download</Button>
                    <Button size="sm" onClick={() => setTarget(e)} className="gap-1" data-testid={`final-${e.export_id}`}><FileSignature className="size-3.5" /> Record Final</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Card data-testid="final-orders-card"><CardContent className="p-0">
        <div className="px-5 py-3 border-b border-border"><div className="text-lg font-semibold">Final Order Records</div></div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Order No.</TableHead><TableHead>Date</TableHead>
            <TableHead>Signing</TableHead><TableHead>CMS</TableHead><TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {finals.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-6">No final order records yet.</TableCell></TableRow>}
            {finals.map((f) => (
              <TableRow key={f.final_order_id}>
                <TableCell className="font-mono text-xs">{f.final_order_number}</TableCell>
                <TableCell className="text-xs">{f.final_order_date}</TableCell>
                <TableCell><StatusBadge status={f.signing_status} /></TableCell>
                <TableCell><StatusBadge status={f.cms_upload_status} /></TableCell>
                <TableCell><StatusBadge status={f.record_status === "Final" ? "Approved" : f.record_status} /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={!!target} onOpenChange={(o) => { if (!o) setTarget(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Record Final Order</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Final Order Number</Label>
              <Input value={form.final_order_number} onChange={(e) => setForm({ ...form, final_order_number: e.target.value })} data-testid="final-order-number" />
            </div>
            <div>
              <Label className="text-xs">Final Order Date</Label>
              <Input type="date" value={form.final_order_date} onChange={(e) => setForm({ ...form, final_order_date: e.target.value })} data-testid="final-order-date" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)}>Cancel</Button>
            <Button onClick={saveFinal} data-testid="save-final-order-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
